import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import {
  buildContactsQuery,
  buildTileCountQuery,
  CONTACT_TILE_STATUSES,
  isMissingInvitableNowColumn,
  isMissingValidMobileColumn,
  legacyInvitableNow,
  type ContactsQueryOptions,
  type ContactsQueryParams,
  type ContactsQueryPlan,
  type ContactTileCounts,
} from "@/utils/contacts";
import type { ContactListRow } from "@/types/sms";
import { fetchExclusionCreatedPhones, fetchMarketingExclusions } from "@/hooks/sms/useMarketingExclusion";

export const CONTACTS_VIEW = "v_customer_invite_status";

const BASE_COLUMNS =
  "customer_phone, display_name, customer_email, is_loyalty, first_order_at, last_order_at, total_orders, " +
  "contact_id, opt_in_status, messaging_status, never_invite, invite_count, last_invited_at, source, " +
  "is_invitable, history_invite_count, history_last_invited_at";

type ViewRow = Omit<ContactListRow, "source_ref" | "opted_in_at" | "opted_out_at" | "messaging_disabled_reason">;

interface ContactExtras {
  id: string;
  source_ref: string | null;
  opted_in_at: string | null;
  opted_out_at: string | null;
  messaging_disabled_reason: string | null;
}

export interface ContactsPage {
  rows: ContactListRow[];
  total: number;
  /** Whether marketing exclusions exist in this database yet (their migration is applied). */
  exclusionsAvailable: boolean;
}

type QueryError = { code?: string; message?: string } | null;
type ViewQuery = ReturnType<ReturnType<SupabaseClient["from"]>["select"]>;

/** Applies a plan's filters (not its order or range) to a view query. */
function applyFilters(q: ViewQuery, plan: ContactsQueryPlan): ViewQuery {
  for (const column of plan.isNull) q = q.is(column, null);
  if (plan.match) q = q.filter(plan.match[0], "match", plan.match[1]);
  for (const [column, value] of plan.eq) q = q.eq(column, value);
  if (plan.inPhones) q = q.in("customer_phone", plan.inPhones);
  if (plan.notInPhones) q = q.not("customer_phone", "in", `(${plan.notInPhones.join(",")})`);
  if (plan.ilike) q = q.ilike(plan.ilike[0], plan.ilike[1]);
  if (plan.like) q = q.like(plan.like[0], plan.like[1]);
  if (plan.or) q = q.or(plan.or);
  return q;
}

/**
 * Runs `attempt` against the view with valid_mobile and is_invitable_now, and
 * again in the matching legacy mode for each of those columns the view does not
 * have yet (its migration is not applied), so the roster and the tiles degrade
 * together, never apart. Postgres names one missing column per error, hence one
 * retry per column at most.
 */
async function withViewFallback<T extends { error: QueryError }>(
  attempt: (options: ContactsQueryOptions) => PromiseLike<T>,
): Promise<T> {
  let options: ContactsQueryOptions = {};
  let result = await attempt(options);
  for (;;) {
    if (!options.legacyValidMobile && isMissingValidMobileColumn(result.error)) {
      options = { ...options, legacyValidMobile: true };
    } else if (!options.legacyInvitable && isMissingInvitableNowColumn(result.error)) {
      options = { ...options, legacyInvitable: true };
    } else {
      return result;
    }
    result = await attempt(options);
  }
}

/**
 * One server-side page (CONTACTS_PAGE_SIZE rows) of the Marketing › Contacts
 * roster from v_customer_invite_status, with an exact total for the pager. The
 * view is security_invoker, so RLS scopes it to the caller's store. A second,
 * page-scoped read of sms_contacts (at most one page of ids, skipped when the
 * page has no contact rows) adds the "since" dates and the disabled reason the
 * view does not carry. Search and filters travel only in the request, never
 * in the page URL. The store's marketing exclusions (a small, separately read
 * set) mark each row and back the "excluded" filter.
 */
export const useContacts = (params: ContactsQueryParams) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<ContactsPage>({
    queryKey: ["sms-contacts", params],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const exclusions = await fetchMarketingExclusions(sb);
      if (params.status === "excluded" && exclusions.byPhone.size === 0) {
        return { rows: [], total: 0, exclusionsAvailable: exclusions.available };
      }
      const excludedPhones = [...exclusions.byPhone.keys()];
      const exclusionCreatedPhones =
        params.source === "orders" || params.source === "manual" ? await fetchExclusionCreatedPhones(sb) : [];

      let legacy = false;
      const { data, count, error } = await withViewFallback((options) => {
        legacy = !!options.legacyInvitable;
        const plan = buildContactsQuery(params, { ...options, excludedPhones, exclusionCreatedPhones });
        const columns = legacy ? BASE_COLUMNS : `${BASE_COLUMNS}, is_invitable_now`;
        let q = applyFilters(sb.from(CONTACTS_VIEW).select(columns, { count: "exact" }), plan);
        for (const o of plan.order) q = q.order(o.column, { ascending: o.ascending, nullsFirst: o.nullsFirst });
        return q.range(plan.range[0], plan.range[1]);
      });
      if (error) throw error;
      const viewRows = ((data ?? []) as unknown as ViewRow[]).map((r) =>
        legacy ? { ...r, is_invitable_now: legacyInvitableNow(r) } : r,
      );

      const ids = viewRows.map((r) => r.contact_id).filter((id): id is string => !!id);
      const extras = new Map<string, ContactExtras>();
      if (ids.length > 0) {
        const { data: contactRows, error: extrasError } = await sb
          .from("sms_contacts")
          .select("id, source_ref, opted_in_at, opted_out_at, messaging_disabled_reason")
          .in("id", ids);
        if (extrasError) throw extrasError;
        for (const c of (contactRows ?? []) as ContactExtras[]) extras.set(c.id, c);
      }

      const rows = viewRows.map((r) => {
        const extra = r.contact_id ? extras.get(r.contact_id) : undefined;
        return {
          ...r,
          source_ref: extra?.source_ref ?? null,
          opted_in_at: extra?.opted_in_at ?? null,
          opted_out_at: extra?.opted_out_at ?? null,
          messaging_disabled_reason: extra?.messaging_disabled_reason ?? null,
          marketing_excluded: exclusions.byPhone.has(r.customer_phone),
          marketing_excluded_at: exclusions.byPhone.get(r.customer_phone) ?? null,
        };
      });
      return { rows, total: count ?? rows.length, exclusionsAvailable: exclusions.available };
    },
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
};

/**
 * The four roster tile counts, each an exact count over the same view with the
 * same plan the roster runs when that tile's filter is applied (valid mobiles
 * only, not deleted), so a tile's number is always the number of rows it lists.
 * `limit(0)` rather than a HEAD request so a missing-column error keeps its body.
 */
export const useContactTileCounts = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<ContactTileCounts>({
    queryKey: ["sms-contacts", "tile-counts"],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const entries = await Promise.all(
        CONTACT_TILE_STATUSES.map(async (status) => {
          const { count, error } = await withViewFallback((options) =>
            applyFilters(
              sb.from(CONTACTS_VIEW).select("customer_phone", { count: "exact" }),
              buildTileCountQuery(status, options),
            ).limit(0),
          );
          if (error) throw error;
          return [status, count ?? 0] as const;
        }),
      );
      return Object.fromEntries(entries) as ContactTileCounts;
    },
    staleTime: 60 * 1000,
  });
};
